import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatIconModule } from '@angular/material/icon';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxUploaderModule, UploadFile, UploadInput, UploadOutput, UploaderOptions } from 'ngx-uploader-esb';
import { take } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { PlaylistItemDTO } from '../../core/models/view-models/playlist-item-models/playlist-item-dto.model';
import { FileUploadingEnum } from '../../core/enums/file-uploading.enum';
import { FilesService } from '../../core/services/http-services/files.service';
import { CustomFileModel } from '../../core/models/entities/file.type';
import { AppConstants } from '../../app.constants';
import { ModalConfigureRssComponent } from '../../shared-module/components/_modals/modal-configure-rss/modal-configure-rss.component';
import { RssTickerConfigs } from '../../core/models/entities/rss-ticker.type';
import { ToastrMessageEnum } from '../../core/enums/toastr-messages/toastr-message.enum';
import { PlaylistDTO } from '../../core/models/view-models/playlist-models/playlist-dto.model';

@Component({
  selector: 'rss-source',
  templateUrl: './rss-source.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgxUploaderModule,
    MatTooltipModule,
    MatIconModule
  ],
  styleUrls: ['./rss-source.component.scss']
})
export class RssSourceComponent implements OnInit {
  @Input() playlistItem: PlaylistItemDTO;
  @Input() playlist: PlaylistDTO;
  @Input() className: string;

  uploadedRssFile: UploadFile;
  options: UploaderOptions;
  rssInputPlaceholder = AppConstants.RSS_INPUT_PLACEHOLDER;
  rssFileTypes = AppConstants.RSS_FILE_TYPES;
  uploadInput: EventEmitter<UploadInput> = new EventEmitter<UploadInput>();

  constructor(
    private filesService: FilesService,
    private dialog: MatDialog,
    private changeRef: ChangeDetectorRef,
    private toastrService: ToastrService
  ) {
    this.filesService.uploadInput$
      .pipe(takeUntilDestroyed())
      .subscribe((uploadData) => {
        this.uploadInput.emit(uploadData)
      });

    this.options = {
      concurrency: 0,
      allowedContentTypes: [...AppConstants.RSS_FILE_TYPES.split(', ')]
    };
  }

  ngOnInit(): void {
    this.playlistItem.RssConfigs.FileName = this.playlistItem.RssConfigs.File?.Title;
  }

  onConfigureRSSTicker(item: PlaylistItemDTO): void {
    const modal = this.dialog.open(ModalConfigureRssComponent, AppConstants.RSS_SETTINGS_DIALOG_WINDOW_CONFIG);
    modal.componentInstance.rssConfigs = {
      ...item.RssConfigs
    };
    modal.componentInstance.isAndroidPlaylist = this.playlist.IsAndroid;

    modal.afterClosed()
      .pipe(take(1))
      .subscribe((data: RssTickerConfigs) => {
        if (data) {
          item.RssConfigs = data;
          this.changeRef.markForCheck();
        }
      });
  }

  onUploadRSS(output: UploadOutput): void {
    switch (output.type) {
      case FileUploadingEnum.AllAddedToQueue:
        if (this.uploadedRssFile) {
          this.filesService.startUpload([this.uploadedRssFile]);
        }
        break;

      case FileUploadingEnum.AddedToQueue:
        if (output.file) {
          this.uploadedRssFile = output.file;
          this.playlistItem.RssConfigs.FileName = output.file.name;
        }
        break;

      case FileUploadingEnum.Done:
        if (output.file.responseStatus === AppConstants.SUCCSESS_REQUEST_RESPONSE_STATUS) {
          const responseFile: CustomFileModel = output.file.response as CustomFileModel;
          this.playlistItem.RssConfigs.FileId = responseFile.Id;
          this.playlistItem.RssConfigs.File = responseFile;
          this.playlistItem.RssConfigs.Url = null;
        }
        else {
          this.toastrService.error(output.file.response.Message, `${ToastrMessageEnum.FailedToUploadTitle}: ${output.file.name}`);
          this.onUploadRSS({ type: FileUploadingEnum.Removed });
        }
        break;

      case FileUploadingEnum.Removed:
        if (
          this.uploadedRssFile &&
          this.uploadedRssFile.responseStatus !== AppConstants.SUCCSESS_REQUEST_RESPONSE_STATUS
        ) {
          this.uploadInput.emit({ type: FileUploadingEnum.Cancel, id: this.uploadedRssFile.id });
        }

        this.uploadedRssFile = null;
        this.playlistItem.RssConfigs.File = null;
        this.playlistItem.RssConfigs.FileId = null;
        this.playlistItem.RssConfigs.FileName = null;

        break;

      case FileUploadingEnum.Rejected:
        this.toastrService.error(ToastrMessageEnum.FileTypeNotSupported, ToastrMessageEnum.RejectedTitle);
        break;
    }
  }

  onRemoveRss(): void {
    this.onUploadRSS({ type: FileUploadingEnum.Removed });
  }
}
